import * as React from 'react'
import * as uuid from 'uuid'
import type { TrackFn } from 'usageTracking'
import { PageResult } from '../pageDirectory'
import { GlobalSearchErrorCode, MIN_QUERY_LENGTH } from '../schema'
import {
    appendSettledStep,
    buildSessionPayload,
    CloseReason,
    createSession,
    groupsToStepGroups,
    markCleared,
    markInputTouched,
    markNextQueryVia,
    markRetry,
    OpenVia,
    QueryVia,
    SearchSession,
    SessionOutcome,
    setOutcome,
    stepResultsFrom,
} from '../searchSession'
import { trackGlobalSearch } from '../tracking'
import { OrderedGroup } from './useGlobalSearch'

export interface SearchSessionController {
    sessionRef: React.MutableRefObject<SearchSession | null>
    onOpen: (via: OpenVia) => void
    // No reason = an open* handler already recorded the outcome.
    onClose: (reason?: CloseReason) => void
    recordOutcome: (outcome: SessionOutcome) => void
    recordQueryVia: (via: QueryVia) => void
    recordRetry: () => void
    recordCleared: () => void
    setLiveQuery: (query: string) => void
}

// Owns the session for one overlay open and sends it exactly once when it
// ends — on close, on a result being opened, on tab unload or on unmount.
// `track` is injectable for tests; production uses the global-search tracker.
export const useSearchSession = (track: TrackFn = trackGlobalSearch): SearchSessionController => {
    const sessionRef = React.useRef<SearchSession | null>(null)
    const queryRef = React.useRef('')
    const trackRef = React.useRef(track)
    trackRef.current = track

    const flush = React.useCallback((opts?: { keepalive?: boolean }) => {
        const session = sessionRef.current
        if (!session || session.sent) {
            return
        }
        session.sent = true
        sessionRef.current = null
        trackRef.current(
            'global_search_session',
            { category: 'global_search', ...buildSessionPayload(session, queryRef.current, Date.now()) },
            opts,
        )
    }, [])

    const abandon = React.useCallback(
        (reason: CloseReason, opts?: { keepalive?: boolean }) => {
            const session = sessionRef.current
            if (!session) {
                return
            }
            setOutcome(session, { kind: 'abandoned', close_reason: reason })
            flush(opts)
        },
        [flush],
    )

    const onOpen = React.useCallback(
        (via: OpenVia) => {
            // A session still here never saw its close — don't lose it.
            abandon('unmount')
            sessionRef.current = createSession(uuid.v4(), via, Date.now())
        },
        [abandon],
    )

    const onClose = React.useCallback(
        (reason?: CloseReason) => {
            if (reason) {
                abandon(reason)
            } else {
                flush()
            }
        },
        [abandon, flush],
    )

    const recordOutcome = React.useCallback((outcome: SessionOutcome) => {
        if (sessionRef.current) {
            setOutcome(sessionRef.current, outcome)
        }
    }, [])

    const recordQueryVia = React.useCallback((via: QueryVia) => {
        if (sessionRef.current) {
            markNextQueryVia(sessionRef.current, via)
        }
    }, [])

    const recordRetry = React.useCallback(() => {
        if (sessionRef.current) {
            markRetry(sessionRef.current)
        }
    }, [])

    const recordCleared = React.useCallback(() => {
        if (sessionRef.current) {
            markCleared(sessionRef.current)
        }
    }, [])

    const setLiveQuery = React.useCallback((query: string) => {
        queryRef.current = query
        if (sessionRef.current && query.trim()) {
            markInputTouched(sessionRef.current)
        }
    }, [])

    // Tab closed/reloaded mid-session, or the overlay unmounted (feature flag,
    // account switch): send what we have. keepalive lets the request outlive
    // the page, same as useDwellTracking.
    React.useEffect(() => {
        const onPageHide = () => abandon('pagehide', { keepalive: true })
        window.addEventListener('pagehide', onPageHide)
        return () => {
            window.removeEventListener('pagehide', onPageHide)
            abandon('unmount', { keepalive: true })
        }
    }, [abandon])

    // Stable identity: the overlay lists the controller in effect deps.
    return React.useMemo(
        () => ({
            sessionRef,
            onOpen,
            onClose,
            recordOutcome,
            recordQueryVia,
            recordRetry,
            recordCleared,
            setLiveQuery,
        }),
        [onOpen, onClose, recordOutcome, recordQueryVia, recordRetry, recordCleared, setLiveQuery],
    )
}

export interface SessionStepInputs {
    isOpen: boolean
    accountSlug: string | null
    hasQuery: boolean
    debouncedQuery: string
    loading: boolean
    fetching: boolean
    orderedGroups: OrderedGroup[]
    pageResults: PageResult[]
    scopedView: PageResult | null
    reservationFallback: PageResult | null
    emailFallback: { query: string; localPart: string } | null
    filtersDropped: boolean
    errorCode: GlobalSearchErrorCode | null
}

// Appends a step each time a debounced query finishes loading — i.e. the user
// paused typing and the search (plus any email fallback) came back. That makes
// every step one real search whose displayed results are known exactly.
export const useSearchSessionSteps = (
    sessionRef: React.MutableRefObject<SearchSession | null>,
    {
        isOpen,
        accountSlug,
        hasQuery,
        debouncedQuery,
        loading,
        fetching,
        orderedGroups,
        pageResults,
        scopedView,
        reservationFallback,
        emailFallback,
        filtersDropped,
        errorCode,
    }: SessionStepInputs,
): void => {
    // Stopwatch for `loading_ms`: runs while a request is in flight and adds
    // up across the requests one debounced query can trigger (primary, email
    // fallback, retry). A new debounced query resets it — time spent on a
    // request the user typed past is not time they waited.
    const fetchStartedAtRef = React.useRef<number | null>(null)
    const pendingLoadingMsRef = React.useRef(0)
    const timedQueryRef = React.useRef(debouncedQuery)
    React.useEffect(() => {
        const now = Date.now()
        if (timedQueryRef.current !== debouncedQuery) {
            timedQueryRef.current = debouncedQuery
            pendingLoadingMsRef.current = 0
            fetchStartedAtRef.current = fetching ? now : null
            return
        }
        if (fetching) {
            fetchStartedAtRef.current ??= now
        } else if (fetchStartedAtRef.current !== null) {
            pendingLoadingMsRef.current += now - fetchStartedAtRef.current
            fetchStartedAtRef.current = null
        }
    }, [fetching, debouncedQuery])

    React.useEffect(() => {
        const session = sessionRef.current
        if (!isOpen || !session || !hasQuery || loading || debouncedQuery.length < MIN_QUERY_LENGTH) {
            return
        }
        const loadingMs = pendingLoadingMsRef.current
        pendingLoadingMsRef.current = 0
        const results = stepResultsFrom(pageResults, orderedGroups, accountSlug)
        appendSettledStep(
            session,
            {
                query: debouncedQuery,
                loading_ms: loadingMs,
                groups: groupsToStepGroups(orderedGroups),
                results,
                scoped_view: scopedView !== null,
                reservation_fallback: reservationFallback !== null,
                email_fallback_used: emailFallback !== null,
                filters_dropped: filtersDropped,
                error: errorCode,
                empty: errorCode === null && results.length === 0,
            },
            Date.now(),
        )
    }, [
        sessionRef,
        isOpen,
        accountSlug,
        hasQuery,
        debouncedQuery,
        loading,
        orderedGroups,
        pageResults,
        scopedView,
        reservationFallback,
        emailFallback,
        filtersDropped,
        errorCode,
    ])
}
